import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, MessageCircle } from 'lucide-react';

export default function CompassionateDialog() {
  const navigate = useNavigate();

  const dialogSteps = [
    {
      speaker: 'Person A',
      message: '"What I would like to tell you is (using OFNR & ≤40 words) ..."',
      request: '"Would you please tell me what you heard me say?"',
      color: 'from-blue-900/30 to-cyan-900/30',
      borderColor: 'border-blue-700/30'
    },
    {
      speaker: 'Person B',
      message: '"Thank you for sharing that with me."\n"What I heard you say was…"\n"Is that right?" (wait for response)\n"Is there more?"',
      label: 'Reflect it back!',
      color: 'from-purple-900/30 to-pink-900/30',
      borderColor: 'border-purple-700/30'
    },
    {
      speaker: 'Person A',
      message: '"Thank you for hearing me."\n\n"Yes, that\'s right and I would also like to say (using OFNR & ≤40 words)"\n\nOR\n\n"No, that\'s not quite it. What I am trying to say..."\n"Would you please tell me what you heard me say?"',
      color: 'from-blue-900/30 to-cyan-900/30',
      borderColor: 'border-blue-700/30'
    },
    {
      speaker: 'Person B',
      message: '"Thank you for sharing that with me."\n"What I heard you say was…"\n"Is that right?" (wait for response)\n"Is there more?"',
      label: 'Reflect it back!',
      color: 'from-purple-900/30 to-pink-900/30',
      borderColor: 'border-purple-700/30'
    },
    {
      speaker: 'Person A',
      message: '"Thank you for hearing me. I\'m complete for now."\n"Would you please tell me what is going on for you having heard me say this?"',
      color: 'from-blue-900/30 to-cyan-900/30',
      borderColor: 'border-blue-700/30'
    },
    {
      speaker: 'Person B',
      message: '"Thank you for asking."\n\n"What\'s going on for me having heard you say that is…"\n"Would you please tell me what you heard me say?"',
      color: 'from-purple-900/30 to-pink-900/30',
      borderColor: 'border-purple-700/30'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">A Compassionate Communication Dialog</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 13</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="text-center mb-12">
            <MessageCircle className="h-20 w-20 text-green-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">Slow it down! Chunk it down!</h2>
            <p className="text-xl text-slate-300 leading-relaxed">
              This structured dialog helps ensure both people feel fully heard and understood. 
              Take turns speaking and reflecting back what you heard.
            </p>
          </div>

          {/* Dialog Flow */}
          <div className="space-y-8 mb-12">
            {dialogSteps.map((step, idx) => (
              <div key={idx} className="relative">
                {/* Label if present */}
                {step.label && (
                  <div className="text-center mb-4">
                    <span className="bg-amber-900/40 text-amber-200 px-6 py-2 rounded-full text-lg font-bold border border-amber-700/30">
                      {step.label}
                    </span>
                  </div>
                )}
                
                {/* Dialog Box */}
                <div className={`bg-gradient-to-br ${step.color} border ${step.borderColor} rounded-lg p-6`}>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${step.color} border ${step.borderColor} flex items-center justify-center`}>
                        <span className="text-white font-bold">{step.speaker === 'Person A' ? 'A' : 'B'}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-400 mb-2 font-semibold">{step.speaker}</p>
                      <div className="text-white leading-relaxed whitespace-pre-line">
                        {step.message}
                      </div>
                      {step.request && (
                        <div className="mt-3 text-white leading-relaxed italic">
                          {step.request}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Arrow between steps */}
                {idx < dialogSteps.length - 1 && (
                  <div className="flex justify-center my-4">
                    <div className="text-slate-600 text-3xl">↓</div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Key Principles */}
          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-700/30 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-6">Key Principles</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-700/30 rounded-full flex items-center justify-center text-green-200">
                  1
                </div>
                <div>
                  <p className="text-white font-semibold">Use OFNR & Keep it Short</p>
                  <p className="text-slate-300 text-sm">Share Observations, Feelings, Needs, Requests in 40 words or less</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-700/30 rounded-full flex items-center justify-center text-green-200">
                  2
                </div>
                <div>
                  <p className="text-white font-semibold">Always Reflect Back</p>
                  <p className="text-slate-300 text-sm">Say what you heard to ensure mutual understanding</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-700/30 rounded-full flex items-center justify-center text-green-200">
                  3
                </div>
                <div>
                  <p className="text-white font-semibold">Confirm & Continue</p>
                  <p className="text-slate-300 text-sm">Ask "Is that right?" and "Is there more?" before moving on</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-700/30 rounded-full flex items-center justify-center text-green-200">
                  4
                </div>
                <div>
                  <p className="text-white font-semibold">Express Gratitude</p>
                  <p className="text-slate-300 text-sm">Thank each other throughout the process</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
